"use client";
import { useSectionView } from "@/app/utils/analytics";
import { handleCheckoutSession } from "@/app/utils/checkout-session";
import { sans } from "@/app/utils/fonts";
import { DISCOUNT_ID, PRICING_ID_T1, PRICING_NAME_T1 } from "@/lib/constants";
import { motion } from "motion/react";

const FEATURES = [
  "AI-native: AGENTS.md + agent recipes built in",
  "Cross-browser support for Chrome, Firefox, and Edge",
  "Stripe payments + subscriptions, fully wired",
  "Firebase auth with Google and email sign-in",
  "Production-ready React + WXT boilerplate",
  "Ready-to-use UI components",
  "Web Store submission guides + asset templates",
  "Lifetime updates",
];

function Pricing() {
  const sectionRef = useSectionView("pricing");
  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden bg-ink px-6 py-24"
      id="pricing"
    >
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="bg-dot-grid absolute inset-0 [mask-image:radial-gradient(640px_circle_at_50%_30%,black,transparent)]" />
        <div className="absolute left-1/2 top-10 h-80 w-[760px] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(11,99,222,0.14),transparent_70%)] blur-2xl" />
      </div>

      <div className="mx-auto flex max-w-6xl flex-col items-center">
        {/* badge */}
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-line bg-panel/70 px-4 py-1.5 text-sm font-medium text-zinc-300 shadow-sm backdrop-blur">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-primary"
          >
            <rect width="20" height="12" x="2" y="6" rx="2" />
            <circle cx="12" cy="12" r="2" />
            <path d="M6 12h.01M18 12h.01" />
          </svg>
          Pricing
        </div>

        <h2
          className={`${sans.className} max-w-2xl text-center text-4xl font-medium tracking-tight text-white md:text-5xl`}
        >
          One price.{" "}
          <span className="text-primary">Everything you need to ship and get paid.</span>
        </h2>
        <p className="mt-3 text-zinc-400">
          Love it or your money back, 30-day guarantee.
        </p>

        {/* card */}
        <motion.div className="relative mt-12 w-full max-w-md">
          <div className="absolute -inset-3 -z-10 rounded-3xl bg-[radial-gradient(ellipse_at_center,rgba(11,99,222,0.22),transparent_70%)] blur-2xl" />
          <div className="overflow-hidden rounded-2xl border border-primary/30 bg-panel p-8 shadow-2xl shadow-black/40 ring-1 ring-primary/20">
            <h3
              className={`${sans.className} text-xl font-semibold text-white`}
            >
              ExtensionFast Starter Kit
            </h3>
            <p className="mt-1 text-zinc-400">
              Ship a paid, cross-browser extension your AI builds on.
            </p>

            <div className="mt-6">
              <p className="flex items-baseline gap-3">
                <span className="text-2xl font-medium text-zinc-500 line-through decoration-zinc-600">
                  $249
                </span>
                <span
                  className={`${sans.className} text-6xl font-bold text-white`}
                >
                  $149
                </span>
                <span className="text-zinc-400">USD</span>
              </p>
              <span className="mt-3 inline-flex items-center rounded-full bg-primary/15 px-3 py-1 text-sm font-semibold text-blue-200 ring-1 ring-primary/30">
                $100 off · one-time payment
              </span>
            </div>

            <ul className="mt-8 flex flex-col gap-3.5 text-[15px] text-zinc-300">
              {FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="mt-0.5 shrink-0 text-primary"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  {f}
                </li>
              ))}
            </ul>

            <button
              onClick={() =>
                handleCheckoutSession(
                  PRICING_ID_T1,
                  DISCOUNT_ID,
                  PRICING_NAME_T1,
                  "pricing",
                )
              }
              className="mt-8 w-full rounded-full bg-primary px-8 py-3.5 text-base font-semibold text-white ring-1 ring-inset ring-white/15 shadow-[0_12px_40px_-10px_rgba(11,99,222,0.65)] transition-all duration-200 hover:bg-secondary active:scale-[0.98]"
            >
              Get ExtensionFast →
            </button>

            <div className="mt-3 flex items-center justify-center gap-2 text-sm text-zinc-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
                <path d="m9 12 2 2 4-4" />
              </svg>
              Stripe secured payment
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default Pricing;
